import React, { forwardRef, InputHTMLAttributes, useImperativeHandle, useMemo, useRef, useState } from "react"
import classnames from "classnames"
import isEmpty from "lodash/isEmpty"
import ImageWrapper from "../../macro-component/ImageWrapper"

type TInputState = "default" | "success" | "warning" | "error"
type TInputWidthType = "sm" | "md" | "lg"

type TTypeObject = {
  [key in TInputWidthType]: string
}

interface ITextInputClasses {
  fontSize: TTypeObject
  margin: TTypeObject
  padding: TTypeObject
  border: TTypeObject
}

const classes: ITextInputClasses = {
  fontSize: {
    sm: "text-sm leading-5",
    md: "text-sm leading-5",
    lg: "text-base leading-6",
  },
  margin: {
    sm: "mb-3",
    md: "mb-4",
    lg: "mb-5",
  },
  padding: {
    sm: "py-2.5 px-3",
    md: "py-3 px-3.5",
    lg: "p-3.5",
  },
  border: {
    sm: "rounded-md",
    md: "rounded-lg",
    lg: "rounded-xl",
  },
}

export interface TextInputRef {
  focus: () => void
  clear: () => void
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  widthType?: TInputWidthType
  label?: string
  helperText?: string
  activeState?: TInputState
  iconName?: string
  iconColor?: string
  containerStyleClass?: string
  inputStyleClass?: string
  dataTestId?: string
  required?: boolean
  textInsideInput?: string
  handleTextInsideInput?: () => void
  textInsideInputStyle?: string
  options?: object
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
}

const TextInput = forwardRef<TextInputRef, TextInputProps>((props, ref) => {
  const {
    id,
    label = "",
    helperText = "",
    widthType = "md",
    disabled = false,
    activeState = "default",
    iconColor = "",
    iconName = "",
    containerStyleClass = "w-352",
    inputStyleClass = "w-80",
    type = "text",
    value = "",
    placeholder = "",
    autoComplete = "false",
    name = "",
    onChange = () => {},
    dataTestId = "textInput",
    required = false,
    textInsideInput,
    handleTextInsideInput,
    textInsideInputStyle,
    readOnly = false,
    inputMode = "text",
    options = {},
    onPaste = () => {},
  } = props

  const textInputRef = useRef<HTMLInputElement>(null)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textInputRef.current) {
        textInputRef.current.focus()
      }
    },
    clear: () => {
      if (textInputRef.current) {
        textInputRef.current.value = ""
      }
    },
  }))

  const containerClasses = classnames(
    "bg-primary flex flex-row justify-between flex-nowrap gap-2 appearance-none placeholder:text-tertiary border-2 focus:outline-none hover:outline-none",
    classes.padding[widthType],
    classes.border[widthType],
    classes.fontSize[widthType],
    containerStyleClass,
    {
      "bg-neutral-light text-tertiary-light border-none": disabled,
      "text-primary border-primary hover:border-secondary focus:border-secondary":
        !disabled && activeState === "default",
      "text-primary border-positive hover:border-positive focus:border-positive":
        !disabled && activeState === "success",
      "text-primary border-warning hover:border-warning focus:border-warning": !disabled && activeState === "warning",
      "text-primary border-negative hover:border-negative focus:border-negative": !disabled && activeState === "error",
    },
  )

  const inputClasses = classnames(
    `bg-primary appearance-none caret-primary-500 focus:caret-primary-500 placeholder:text-secondary-font border outline-none focus:outline-none hover:outline-none border-none ${inputStyleClass}`,
  )

  const labelClasses = classnames(`text-sm leading-4`, {
    "text-tertiary": disabled,
    "text-primary": !disabled,
  })

  const helperTextClasses = classnames(`text-xs md:text-sm font-medium cursor-default`, {
    "text-tertiary": disabled,
    "text-secondary": !disabled && activeState === "default",
    "text-positive": !disabled && activeState === "success",
    "text-warning": !disabled && activeState === "warning",
    "text-negative": !disabled && activeState === "error",
  })

  const onContainerClick = () => textInputRef.current?.focus()
  const handleEyeClick = () => setShowPassword((prev) => !prev)

  const inputType = useMemo(() => {
    if (type == "password") {
      if (showPassword) return "text"
      return type
    }
    return type
  }, [showPassword, type])

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number" && ["e", "E", "-", "+"].includes(evt.key)) {
      evt.preventDefault()
    }
  }

  return (
    <div className="flex flex-col flex-wrap gap-2 sm:mb-2" onClick={onContainerClick}>
      {!isEmpty(label) && (
        <label htmlFor={id} className={labelClasses}>
          {label}
          {props.required && <span className="text-color-error">*</span>}
        </label>
      )}
      <div className={containerClasses}>
        <input
          id={id}
          name={name}
          type={inputType}
          value={value}
          placeholder={placeholder}
          ref={textInputRef}
          className={type != "radio" ? inputClasses : ""}
          disabled={disabled}
          autoComplete={autoComplete}
          onChange={onChange}
          onPaste={onPaste}
          data-testid={dataTestId}
          required={required}
          readOnly={readOnly}
          inputMode={inputMode}
          onKeyDown={handleKeyDown}
          {...options}
        />
        {!isEmpty(iconName) && (
          <ImageWrapper url="" color={iconColor} name={iconName} width={20} height={20} alt_text={"x"} />
        )}
        {type == "password" && (
          <div onClick={handleEyeClick} className="cursor-pointer">
            <ImageWrapper
              url=""
              color={iconColor}
              name={showPassword ? "eye_bold" : "eye_slash_bold"}
              width={24}
              height={24}
              alt_text={"x"}
            />
          </div>
        )}
        {textInsideInput && (
          <label onClick={handleTextInsideInput} className={textInsideInputStyle}>
            {" "}
            {textInsideInput}{" "}
          </label>
        )}
      </div>
      {!isEmpty(helperText) && <label className={helperTextClasses}> {helperText} </label>}
    </div>
  )
})

export default TextInput
